import { useCallback, useMemo, useState } from 'react';
import type { IMasterScheduleRow } from '@interfaces/IMasterSchedule';

export interface IMasterScheduleCollapse {
  /** Keys of rows that have descendant rows beneath them (render a chevron). */
  collapsibleKeys: ReadonlySet<string>;
  /** Keys of rows the user has collapsed. */
  collapsedKeys: ReadonlySet<string>;
  /** Rows after hiding everything nested under a collapsed ancestor. */
  visibleRows: IMasterScheduleRow[];
  /** Toggle one row's collapsed state. */
  toggle: (key: string) => void;
}

/** Session-local collapse/expand state for the hierarchical Gantt rows. A row is
 *  collapsible when the next row in the pre-order list is deeper than it. */
export function useMasterScheduleCollapse(rows: readonly IMasterScheduleRow[]): IMasterScheduleCollapse {
  const [collapsedKeys, setCollapsedKeys] = useState<ReadonlySet<string>>(() => new Set());

  // Stable so memoized FrozenRows don't re-render on unrelated updates.
  const toggle = useCallback((key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const collapsibleKeys = useMemo(() => {
    const keys = new Set<string>();
    for (let i = 0; i < rows.length - 1; i++) {
      if (rows[i + 1]!.depth > rows[i]!.depth) keys.add(rows[i]!.key);
    }
    return keys;
  }, [rows]);

  const visibleRows = useMemo(() => {
    const out: IMasterScheduleRow[] = [];
    let hideBelow = Infinity;
    for (const row of rows) {
      if (row.depth > hideBelow) continue;
      hideBelow = Infinity;
      out.push(row);
      if (collapsedKeys.has(row.key)) hideBelow = row.depth;
    }
    return out;
  }, [rows, collapsedKeys]);

  return { collapsibleKeys, collapsedKeys, visibleRows, toggle };
}
